import React, { useEffect } from 'react';
import { X, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { useSiteConfig, resolveImageUrl } from '../../context/SiteConfigContext';

const PALETTE = {
  nord: { accent: '#1e5e46', gradient: 'linear-gradient(180deg, rgba(30,94,70,0.9) 0%, rgba(18,59,44,0.98) 100%)' },
  sud: { accent: '#1c8a9c', gradient: 'linear-gradient(180deg, rgba(28,138,156,0.9) 0%, rgba(19,95,109,0.98) 100%)' },
  plage: { accent: '#d68a3c', gradient: 'linear-gradient(180deg, rgba(214,138,60,0.9) 0%, rgba(181,106,36,0.98) 100%)' },
  banana: { accent: '#c99a1e', gradient: 'linear-gradient(180deg, rgba(236,194,60,0.9) 0%, rgba(201,154,30,0.98) 100%)' },
  transfert: { accent: '#2b4a72', gradient: 'linear-gradient(180deg, rgba(43,74,114,0.9) 0%, rgba(26,46,74,0.98) 100%)' },
};

const TourModal = ({ circuit, onClose }) => {
  const { lang } = useLang();
  const { config } = useSiteConfig();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!circuit) return null;

  const content = circuit.translations?.[lang] || {};
  const p = PALETTE[circuit.color] || PALETTE.nord;
  const modalT = config?.modal?.[lang] || { stops_title: '', close: 'Close', book_whatsapp: 'WhatsApp' };
  const csection = config?.circuitsSection?.[lang] || { per: '' };

  const primaryContact = (config?.contacts || []).find((c) => (c.showInLangs || []).includes(lang)) || (config?.contacts || [])[0];
  const whatsappNumber = primaryContact?.whatsapp || '596696396739';
  const waText = encodeURIComponent(
    lang === 'fr' ? `Bonjour, je suis intéressé(e) par le circuit « ${content.title || ''} ». `
    : lang === 'en' ? `Hi, I'm interested in the "${content.title || ''}" tour. `
    : lang === 'es' ? `Hola, me interesa el circuito "${content.title || ''}". `
    : `Hallo, ich interessiere mich für die Tour „${content.title || ''}“. `
  );
  const bookHref = `https://wa.me/${whatsappNumber}?text=${waText}`;

  const imgUrl = resolveImageUrl(content.image || circuit.image);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200" />

      <div
        className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl bg-[#0e2a24] text-white flex flex-col animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Image header */}
        <div className="relative h-56 md:h-72 shrink-0">
          {imgUrl && (
            <img src={imgUrl} alt={content.title || ''} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0" style={{ background: p.gradient }} />

          <button
            onClick={onClose}
            aria-label={modalT.close}
            className="absolute top-4 right-4 z-10 grid place-items-center w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur transition-colors"
          >
            <X size={18} />
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <p className="text-[10px] tracking-[0.28em] uppercase text-white/80">{content.badge}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl leading-tight">{content.title}</h2>
            <p className="mt-1 text-[13px] text-white/85">{content.subtitle}</p>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 md:p-8">
          {content.description && (
            <p className="text-[14.5px] leading-relaxed text-white/85">{content.description}</p>
          )}

          {(content.stops || []).length > 0 && (
            <>
              <div className="mt-7 text-[10px] tracking-[0.25em] uppercase text-white/50">{modalT.stops_title}</div>
              <ul className="mt-3 space-y-2">
                {content.stops.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14px] text-white/80">
                    <MapPin size={14} className="mt-1 opacity-70 shrink-0" style={{ color: p.accent }} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Meta */}
          <div className="mt-7 flex flex-wrap items-center gap-4 text-[12px] text-white/60 font-mono">
            {circuit.start && circuit.start !== '—' ? (
              <span>{circuit.start} → {circuit.end}</span>
            ) : (
              <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {circuit.duration}</span>
            )}
            <span className="opacity-50">•</span>
            <span>{circuit.price}€ {csection.per}</span>
          </div>

          <a
            href={bookHref}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1eb857] text-white font-medium px-5 py-3 text-sm transition-colors"
          >
            <MessageCircle size={16} />
            {modalT.book_whatsapp}
          </a>
        </div>
      </div>
    </div>
  );
};

export default TourModal;
